import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, ChevronDown, Check, X, ChevronRight, Layers } from 'lucide-react'
import {
  ensureWorkspace,
  restartAppWorkspaceContainer,
  recreateAppWorkspaceContainer,
  rebuildApp,
  getApp,
  listAppSessions,
  saveWorkspaceChanges,
  getImportStatus,
  getImportTimeline,
  trackWorkspaceOpen,
  type App,
  type Workspace as WorkspaceInfo,
  type AppSession,
  type ImportStatus,
  type ImportTimelineEvent,
} from '../lib/api'
import { PanelType } from '../components/workspace/PanelToggle'
import WorkspaceLayout from '../components/workspace/WorkspaceLayout'
import MobileWorkspaceLayout from '../components/workspace/MobileWorkspaceLayout'
import WorkspaceContextMenu from '../components/workspace/WorkspaceContextMenu'
import PanelMenu from '../components/workspace/PanelMenu'
import { useIsMobile } from '../hooks/useMediaQuery'
import { useVisualViewport } from '../hooks/useVisualViewport'
import { useWorkspaceFocusHeartbeat } from '../hooks/useWorkspaceFocusHeartbeat'
import HelpWidget from '../components/HelpWidget'
import BetaBadge from '../components/BetaBadge'
import { useSettings } from '../hooks/useSettings'
import { buildWorkspacePreviewUrl } from '../lib/workspacePreview'

// Available panels constant (docs added conditionally inside component based on appName)

// Storage keys for panel preferences (different for app vs general workspace)
const PANEL_PREFS_KEY = 'workspace-panel-prefs'
const APP_PANEL_PREFS_KEY = 'workspace-app-panel-prefs'

function loadPanelPrefs(hasApp: boolean): PanelType[] {
  const storageKey = hasApp ? APP_PANEL_PREFS_KEY : PANEL_PREFS_KEY
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Validate that parsed value is an array (old format was different)
      if (Array.isArray(parsed)) {
        return parsed
      }
      // Clear old/invalid format
      localStorage.removeItem(storageKey)
    }
  } catch (e) {
    // Clear corrupted data
    localStorage.removeItem(storageKey)
  }
  // Default panels based on context:
  // - App workspace: Preview panel (local iframe browser)
  // - General workspace: Server Browser panel (Xpra)
  return hasApp ? ['preview'] : ['browser']
}

function savePanelPrefs(prefs: PanelType[], hasApp: boolean) {
  const storageKey = hasApp ? APP_PANEL_PREFS_KEY : PANEL_PREFS_KEY
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs))
  } catch (e) {
    // Ignore
  }
}

function formatImportEventTime(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function labelImportStage(stage: string | null | undefined): string {
  switch ((stage || '').toLowerCase()) {
    case 'uploading':
      return 'Upload'
    case 'cloning':
      return 'Clone'
    case 'converting':
      return 'Analysis'
    case 'deploying':
      return 'Deploy'
    case 'verify':
      return 'Verify'
    case 'complete':
      return 'Complete'
    case 'failed':
      return 'Failed'
    default:
      return 'Preparing'
  }
}

// Work area dropdown for switching between sessions in the workspace toolbar.
// "Session" is kept in code/API for backward compatibility (D-03); the user-facing
// icon and strings use the neutral "work area" vocabulary (D-01, ISSUE-U01).
function SessionDropdown({
  appName,
  activeSession,
  onSelectSession,
}: {
  appName: string
  activeSession: string
  onSelectSession: (sessionName: string) => void
}) {
  const [sessions, setSessions] = useState<AppSession[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch sessions on mount and every 30 seconds
  useEffect(() => {
    const load = async () => {
      try {
        const result = await listAppSessions(appName)
        setSessions(result.sessions)
      } catch {
        // Ignore fetch errors silently
      }
    }
    load()
    intervalRef.current = setInterval(load, 30000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [appName])

  const activeSessions = sessions.filter(s => s.status !== 'archived')

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        title="Switch work area"
        className="flex items-center gap-1.5 text-sm text-foreground/70 hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-secondary"
      >
        <Layers className="w-3.5 h-3.5" />
        <span>{activeSession}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[200px]">
            {activeSessions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No work areas</div>
            ) : (
              activeSessions.map(session => (
                <button
                  key={session.id}
                  onClick={() => {
                    onSelectSession(session.name)
                    setIsOpen(false)
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-secondary transition-colors flex items-center gap-2"
                >
                  <Layers className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{session.name}</span>
                  {session.name === activeSession && (
                    <Check className="w-3.5 h-3.5 text-primary ml-auto flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default function Workspace() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const visualViewport = useVisualViewport()
  const { settings, updateSettings: updateSettingsBatch } = useSettings()

  // Get folder parameter for initial working directory
  const folder = searchParams.get('folder')
  // Get app URL for browser panel
  const appUrl = searchParams.get('url')
  // Get app name (either explicit param or extracted from folder path)
  const appNameParam = searchParams.get('app')
  // Extract app name from folder path like /home/coder/workspace/apps/{name}
  const extractedAppName = folder?.match(/(?:\/home\/coder\/)?workspace\/apps\/([^/]+)/)?.[1]
  const appName = appNameParam || extractedAppName
  // Server-side claude_* notification suppression while this tab is focused.
  // See services/router/shared/workspace_focus.py for the why.
  useWorkspaceFocusHeartbeat(appName)

  // Audit "workspace_open" once per (user, app, minute). Without this the
  // activity feed had no signal that someone actually opened the IDE for
  // an app — workspace_session_create only fires on new session worktrees.
  useEffect(() => {
    if (!appName) return
    trackWorkspaceOpen(appName).catch(() => {})
  }, [appName])
  // Optional import id for showing background import progress after redirect
  const importId = useMemo(() => {
    const raw = searchParams.get('import_id')
    if (!raw) return null
    const parsed = Number(raw)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }, [searchParams])
  // Get session parameter (contains terminal URL with tmux session arg)
  // e.g., "/workspace/app/myapp/terminal/?arg=main"
  const sessionParam = searchParams.get('session')
  // Extract the tmux session name from the session URL's arg parameter
  const tmuxSession = sessionParam ? new URLSearchParams(sessionParam.split('?')[1] || '').get('arg') : undefined

  // Session state: activeSession tracks the currently displayed session,
  // requestedSession is the "intent" that TerminalTabs consumes to switch tabs
  const [activeSession, setActiveSession] = useState(tmuxSession || 'main')
  const [requestedSession, setRequestedSession] = useState<string | undefined>(undefined)

  // Handle session selection from dropdown
  const handleSelectSession = useCallback((sessionName: string) => {
    setRequestedSession(sessionName)
    setActiveSession(sessionName)
  }, [])

  // When TerminalTabs acknowledges a session change via onSessionChange callback
  const handleSessionChange = useCallback((sessionName: string) => {
    setActiveSession(sessionName)
  }, [])

  // Workspace state
  const [workspaceStatus, setWorkspaceStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [, setWorkspace] = useState<WorkspaceInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importProgress, setImportProgress] = useState<ImportStatus | null>(null)
  const [importEvents, setImportEvents] = useState<ImportTimelineEvent[]>([])
  const [dismissedImportBannerId, setDismissedImportBannerId] = useState<number | null>(null)
  const [restarting, setRestarting] = useState(false)
  const [recreating, setRecreating] = useState(false)
  const [rebuilding, setRebuilding] = useState(false)
  const [app, setApp] = useState<App | null>(null)
  const appTitle = app?.display_name || app?.name || null
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [workspaceNotice, setWorkspaceNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)

  // Compute effective preview URL based on active session:
  // - main session: use dev route (/a/dev/{appName}/) so preview always shows
  //   the live dev container, not the frozen production build
  // - non-main sessions: use branch preview URL (/a/branch/{sessionName}/{appName}/)
  const effectivePreviewUrl = useMemo(() => {
    if (!appUrl) return undefined

    const resolvedUrl = buildWorkspacePreviewUrl(
      appUrl,
      appName,
      activeSession,
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    ) || appUrl

    // Force a one-time iframe reload when import reaches complete so preview
    // switches off the temporary "Importing..." page without manual refresh.
    if (importId && importProgress?.status === 'complete') {
      try {
        const parsed = new URL(resolvedUrl)
        parsed.searchParams.set('cc_import_complete', importProgress.updated_at || String(importId))
        return parsed.toString()
      } catch {
        return resolvedUrl
      }
    }

    return resolvedUrl
  }, [appUrl, appName, activeSession, importId, importProgress?.status, importProgress?.updated_at])

  // Panel preferences (context-aware based on app vs general workspace)
  const hasApp = Boolean(appName)
  const [panelPrefs, setPanelPrefs] = useState<PanelType[]>(() => loadPanelPrefs(hasApp))
  const activePanels = useMemo(() => new Set(panelPrefs), [panelPrefs])
  const availablePanels = useMemo(() => {
    const panels: PanelType[] = ['preview', 'browser', 'files']
    if (appName) {
      panels.push('docs')
      panels.push('git')
    }
    return panels
  }, [appName])

  // Ensure workspace is ready and user has edit permission
  useEffect(() => {
    let mounted = true

    async function checkWorkspace() {
      try {
        setWorkspaceStatus('loading')

        // Check if user has edit permission on the app
        if (appName) {
          try {
            const fetched = await getApp(appName)
            if (mounted) {
              setApp(fetched)
            }
            const myPermission = fetched.my_permission ?? 'owner'
            const canEdit = ['owner', 'admin', 'edit'].includes(myPermission)

            if (!canEdit) {
              // View-only users should be redirected to the app URL
              if (fetched.url) {
                window.location.href = fetched.url
                return
              }
              // If no URL available, navigate to apps page
              navigate('/apps')
              return
            }
          } catch (err) {
            // If we can't fetch the app, likely unauthorized
            console.error('Failed to verify app permission:', err)
            if (mounted) {
              setError('You do not have permission to access this workspace')
              setWorkspaceStatus('error')
            }
            return
          }
        }

        const ws = await ensureWorkspace()
        if (mounted) {
          if (!appName) {
            setApp(null)
          }
          setWorkspace(ws)
          setWorkspaceStatus('ready')
        }
      } catch (err) {
        // Nested imported platforms may not expose the global /api/workspace
        // bootstrap endpoint, but app-scoped workspace routes can still work.
        if (appName && err instanceof Error && err.message.toLowerCase().includes('not found')) {
          if (mounted) {
            setWorkspace(null)
            setWorkspaceStatus('ready')
          }
          return
        }
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize workspace')
          setWorkspaceStatus('error')
        }
      }
    }

    checkWorkspace()

    return () => {
      mounted = false
    }
  }, [appName, navigate])

  // Track workspace visit for onboarding checklist
  useEffect(() => {
    if (settings?.dashboard?.checklist_step_workspace !== true) {
      updateSettingsBatch({
        dashboard: { ...settings?.dashboard, checklist_step_workspace: true },
      }).catch(() => {
        // Non-critical
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Poll import status (when workspace is opened from import flow)
  useEffect(() => {
    if (!importId) {
      setImportProgress(null)
      setImportEvents([])
      return
    }

    let cancelled = false
    let interval: ReturnType<typeof setInterval> | null = null
    setImportEvents([])

    const poll = async () => {
      try {
        const status = await getImportStatus(importId)
        let timelineEvents: ImportTimelineEvent[] | null = null
        try {
          const timeline = await getImportTimeline(importId, 40)
          timelineEvents = timeline.events
        } catch {
          // Keep polling resilient and preserve the last good timeline
          // instead of resetting the banner back to generic text.
        }
        if (cancelled) return
        setImportProgress(status)
        if (timelineEvents !== null) {
          setImportEvents(timelineEvents)
        }
        if (status.status === 'complete' || status.status === 'failed') {
          if (interval) {
            clearInterval(interval)
            interval = null
          }
        }
      } catch {
        // Keep polling on transient fetch errors
      }
    }

    void poll()
    interval = setInterval(() => {
      void poll()
    }, 2000)

    return () => {
      cancelled = true
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [importId])

  const importBanner = useMemo(() => {
    if (!importId || !importProgress) return null
    if (dismissedImportBannerId === importId) return null

    const status = importProgress.status
    const isFailed = status === 'failed'
    const isComplete = status === 'complete'
    const isInProgress = !isFailed && !isComplete

    let title = 'Import in progress'
    let detail = 'Preparing workspace and project files'
    const stageEvents = importEvents.filter(event => Boolean(event.message))
    const latestEvent = stageEvents[stageEvents.length - 1] || null
    const recentEvents = stageEvents.slice(-4)
    const latestStageLabel = labelImportStage(latestEvent?.stage || status)
    const latestMessage = latestEvent?.message?.trim()
    if (status === 'cloning') {
      title = 'Cloning repository'
      detail = 'Fetching source code into the workspace'
    } else if (status === 'converting') {
      title = 'Analyzing repository'
      detail = 'Determining how to run the project on the platform'
    } else if (status === 'deploying') {
      title = 'Deploying app'
      detail = 'Building and verifying app startup'
    } else if (status === 'uploading') {
      title = 'Uploading project'
      detail = 'Receiving archive and extracting files'
    } else if (isComplete) {
      title = 'Import complete'
      detail = 'App is ready. You can continue from this workspace.'
    } else if (isFailed) {
      title = 'Import failed'
      detail = importProgress.error_message || 'Open logs in terminal and retry conversion or deployment.'
    }

    if (latestMessage && !isFailed) {
      detail = latestMessage
    }

    const containerClass = isFailed
      ? 'border-destructive/20 bg-destructive/10'
      : isComplete
        ? 'border-green-500/20 bg-green-500/10'
        : 'border-primary/20 bg-primary/5'
    const titleClass = isFailed
      ? 'text-destructive'
      : isComplete
        ? 'text-green-700 dark:text-green-400'
        : 'text-foreground'

    return (
      <div className={`px-4 py-2 border-b ${containerClass}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isInProgress && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              <p className={`text-sm font-medium ${titleClass}`}>{title}</p>
            </div>
            <p className="text-xs text-muted-foreground">{detail}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <span className="rounded-full border border-border/70 px-2 py-0.5">
                Stage: {latestStageLabel}
              </span>
              {latestEvent?.attempt_no ? (
                <span className="rounded-full border border-border/70 px-2 py-0.5">
                  Attempt {latestEvent.attempt_no}
                </span>
              ) : null}
            </div>
            {recentEvents.length > 0 ? (
              <div className="mt-2 space-y-1">
                {recentEvents.map((event) => (
                  <div key={event.id} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                    <span className="font-mono text-[10px] text-muted-foreground/80">
                      {formatImportEventTime(event.created_at)}
                    </span>
                    <span className="min-w-0 break-words">{event.message}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          
            <button title="Dismiss this notification"
              type="button"
              className="mt-0.5 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors"
              onClick={() => setDismissedImportBannerId(importId)}
              aria-label="Dismiss import status banner"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          
        </div>
      </div>
    )
  }, [dismissedImportBannerId, importEvents, importId, importProgress])

  // Handle panel toggle
  const handlePanelToggle = useCallback((panel: PanelType) => {
    setPanelPrefs((prev) => {
      const isActive = prev.includes(panel)
      const newPanels = isActive
        ? prev.filter((p) => p !== panel)
        : [...prev, panel]
      savePanelPrefs(newPanels, hasApp)
      return newPanels
    })
  }, [hasApp])

  // Handle panel close (from within the panel)
  const handlePanelClose = useCallback((panel: PanelType) => {
    handlePanelToggle(panel)
  }, [handlePanelToggle])

  // Handle container restart
  const handleRestart = useCallback(async () => {
    if (!appName) return

    setRestarting(true)
    try {
      const result = await restartAppWorkspaceContainer(appName)
      if (result.status === 'restarted') {
        // Reload the page to reconnect to the restarted container
        window.location.reload()
        return
      }

      // For cooldown/in-progress no-op responses, keep current page/session.
      setRestarting(false)
    } catch (err) {
      console.error('Failed to restart container:', err)
      setRestarting(false)
    }
  }, [appName])

  const handleRecreate = useCallback(async () => {
    if (!appName) return

    setRecreating(true)
    try {
      // Delete only the container (preserves filesystem); the next page load
      // triggers get_or_create which re-pulls the latest image and re-injects
      // platform env vars. Use when a plain restart isn't enough (eg. updated
      // workspace image or new env vars from a Router upgrade).
      await recreateAppWorkspaceContainer(appName)
      window.location.reload()
    } catch (err) {
      console.error('Failed to recreate container:', err)
      setRecreating(false)
    }
  }, [appName])

  // Rebuild the published app (NOT the workspace dev container) — same action
  // as Rebuild on AppDetail. Deploys current main/ to all users, so it's gated
  // by a confirm dialog inside the toolbar context menu.
  const handleRebuild = useCallback(async () => {
    if (!appName) return
    setRebuilding(true)
    setWorkspaceNotice(null)
    try {
      await rebuildApp(appName)
      setWorkspaceNotice({
        tone: 'success',
        text: 'Rebuild started. Watch progress on the App detail page.',
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Rebuild failed.'
      setWorkspaceNotice({ tone: 'error', text: message })
    } finally {
      setRebuilding(false)
    }
  }, [appName])

  const handleSaveCurrentWork = useCallback(async () => {
    if (!appName || saving) return
    const message = saveMessage.trim()
    if (!message) {
      setWorkspaceNotice({ tone: 'error', text: 'Add a short save message first.' })
      return
    }

    setSaving(true)
    setWorkspaceNotice(null)
    try {
      const result = await saveWorkspaceChanges(appName, {
        session_name: activeSession,
        message,
      })
      setSaveDialogOpen(false)
      setSaveMessage('')
      setWorkspaceNotice({
        tone: 'success',
        text: result.saved
          ? `Saved ${result.changed_file_count} ${result.changed_file_count === 1 ? 'file' : 'files'} in ${result.session_name}.`
          : `Nothing to save in ${result.session_name}.`,
      })
    } catch (err) {
      setWorkspaceNotice({
        tone: 'error',
        text: err instanceof Error ? err.message : 'Failed to save changes',
      })
    } finally {
      setSaving(false)
    }
  }, [activeSession, appName, saveMessage, saving])

  // Loading state
  if (workspaceStatus === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Starting workspace...</p>
      </div>
    )
  }

  // Error state
  if (workspaceStatus === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <p className="text-destructive mb-4">{error || 'Failed to start workspace'}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div
      className="flex flex-col h-screen bg-background"
      style={
        // When iOS keyboard is open, h-screen (100vh) does NOT shrink — vh units
        // are sticky to the device's full screen on iOS. Clamp to visualViewport
        // so the entire page tree fits above the keyboard and the terminal prompt
        // stays visible. (Otherwise the bottom of the layout — including the
        // MobileKeyboardBar and live prompt — sits behind the keyboard.)
        isMobile && visualViewport.isKeyboardOpen
          ? { height: visualViewport.height }
          : undefined
      }
    >
      {/* Toolbar */}
      <div className="sticky top-0 z-40 flex items-center justify-between gap-2 px-4 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))] border-b border-border bg-card flex-shrink-0">
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <button title={appName ? 'Back to app details' : 'Return to the home page'}
            onClick={() => navigate(appName ? `/apps/${encodeURIComponent(appName)}` : '/')}
            className="p-1.5 rounded hover:bg-secondary transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            {/* "Apps" + beta badge + first chevron — desktop only; too much chrome on mobile */}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="hidden sm:inline font-medium text-muted-foreground transition-colors hover:text-foreground hover:underline"
            >
              Apps
            </button>
            <span className="hidden sm:inline-flex"><BetaBadge compact /></span>
            {appName && (
              <>
                <ChevronRight className="hidden sm:inline h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <button
                  type="button"
                  onClick={() => navigate(`/apps/${encodeURIComponent(appName)}`)}
                  className="max-w-[10rem] sm:max-w-[18rem] truncate font-medium text-primary transition-colors hover:underline"
                  title={appTitle || appName}
                >
                  {appTitle || appName}
                </button>
              </>
            )}
            {appName && (
              <>
                {/* "Workspace" label only on desktop — on mobile the session dropdown speaks for itself */}
                <ChevronRight className="hidden sm:inline h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <span className="hidden sm:inline font-medium text-foreground">Workspace</span>
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <SessionDropdown
                  appName={appName}
                  activeSession={activeSession}
                  onSelectSession={handleSelectSession}
                />
              </>
            )}
          </div>

        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Two labeled dropdown triggers — Actions first, Panels second. */}
          {app && (
            <WorkspaceContextMenu
              app={app}
              onSaveWork={() => setSaveDialogOpen(true)}
              onRestartWorkspace={handleRestart}
              onRecreateWorkspace={handleRecreate}
              onRebuildApp={handleRebuild}
              saving={saving}
              restarting={restarting}
              recreating={recreating}
              rebuilding={rebuilding}
              onAppUpdated={setApp}
            />
          )}
          <PanelMenu
            availablePanels={availablePanels}
            activePanels={activePanels}
            onToggle={handlePanelToggle}
          />
        </div>
      </div>

      {importBanner}
      {workspaceNotice && (
        <div
          className={`border-b px-4 py-2 text-sm ${
            workspaceNotice.tone === 'success'
              ? 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-300'
              : 'border-destructive/30 bg-destructive/10 text-destructive'
          }`}
        >
          {workspaceNotice.text}
        </div>
      )}

      {/* Workspace content */}
      <div className="flex-1 overflow-hidden">
        {isMobile ? (
          <MobileWorkspaceLayout
            activePanels={activePanels}
            onPanelClose={handlePanelClose}
            cwd={folder || undefined}
            browserUrl={effectivePreviewUrl || undefined}
            appName={appName || undefined}
            tmuxSession={tmuxSession || undefined}
            requestedSession={requestedSession}
            onSessionChange={handleSessionChange}
          />
        ) : (
          <WorkspaceLayout
            activePanels={activePanels}
            onPanelClose={handlePanelClose}
            onRequestSession={handleSelectSession}
            cwd={folder || undefined}
            browserUrl={effectivePreviewUrl || undefined}
            appName={appName || undefined}
            tmuxSession={tmuxSession || undefined}
            requestedSession={requestedSession}
            onSessionChange={handleSessionChange}
          />
        )}
      </div>
      {saveDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void handleSaveCurrentWork()
            }}
            className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-lg"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Save work</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Add a short note for this saved version in {activeSession}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!saving) setSaveDialogOpen(false)
                }}
                disabled={saving}
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
                aria-label="Close save dialog"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <label className="mt-4 block text-sm font-medium" htmlFor="workspace-save-message">
              What changed?
            </label>
            <input
              id="workspace-save-message"
              value={saveMessage}
              onChange={(event) => setSaveMessage(event.target.value)}
              disabled={saving}
              autoFocus
              maxLength={200}
              placeholder="Example: Update checkout form copy"
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary disabled:opacity-50"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveDialogOpen(false)}
                disabled={saving}
                className="rounded-lg border border-border px-4 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !saveMessage.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </div>
          </form>
        </div>
      )}
      {!isMobile && <HelpWidget />}
    </div>
  )
}
